import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import SharedFooter from '../components/SharedFooter';
import { 
  MapPin, Phone, Mail, ChevronRight, ChevronLeft, Menu, X, Instagram, Facebook,
  Image, Eye, Flame, Waves, ThermometerSun, TreePine, Bike, Users, Wifi, 
  Car, Tent, Gamepad2, Baby, Dumbbell, Film, Sparkles, Bath, Star, Gift, Percent,
  Globe, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== PROMO POPUP ==============
const PromoPopup = ({ config }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (!config?.enabled || isDismissed) return;
    
    // Check if already shown in this session
    if (config.show_once_per_session && sessionStorage.getItem('promo_shown')) {
      return;
    }

    const timer = setTimeout(() => {
      setIsVisible(true);
      if (config.show_once_per_session) {
        sessionStorage.setItem('promo_shown', 'true');
      }
    }, (config.show_after_seconds || 5) * 1000);

    return () => clearTimeout(timer);
  }, [config, isDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
  };

  if (!isVisible || !config?.enabled) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div 
        className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300"
        style={{ backgroundColor: config.background_color || '#1A3C34' }}
      >
        {/* Close button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-10"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="relative p-8 text-center">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
            <Gift className="w-8 h-8 text-white" />
          </div>

          {/* Title */}
          <h3 className="font-heading text-2xl md:text-3xl text-white mb-2">
            {config.title || '🎁 Specjalna oferta!'}
          </h3>

          {/* Subtitle */}
          <p className="font-body text-white/80 text-sm mb-4">
            {config.subtitle || 'Tylko dla nowych gości'}
          </p>

          {/* Description */}
          <p className="font-body text-white text-lg mb-6">
            {config.description || 'Zarezerwuj pobyt teraz i otrzymaj 10% zniżki!'}
          </p>

          {/* Discount code if present */}
          {config.discount_code && (
            <div className="mb-6 p-3 bg-white/10 rounded-lg border border-white/20">
              <p className="font-body text-white/70 text-xs mb-1">Twój kod rabatowy:</p>
              <p className="font-heading text-2xl text-white tracking-wider">{config.discount_code}</p>
            </div>
          )}

          {/* CTA Button */}
          <a 
            href={config.button_link || '#contact'}
            onClick={handleDismiss}
            className="inline-flex items-center gap-2 bg-white text-forest px-8 py-3 rounded-full font-heading text-lg hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
          >
            {config.button_text || 'Skorzystaj z oferty'}
            <ChevronRight className="w-5 h-5" />
          </a>

          {/* Skip link */}
          <button 
            onClick={handleDismiss}
            className="block w-full mt-4 font-body text-white/50 text-sm hover:text-white/70 transition-colors"
          >
            Może później
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== NAVBAR ==============
const Navbar = ({ siteLogo, menuItems = [] }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Default menu if none loaded from API
  const defaultLinks = [
    { name: 'O nas', href: '/#about', is_route: false },
    { name: 'Galeria', href: '/#gallery', is_route: false },
    { name: 'Oferta', href: '/#offer', is_route: false },
    { name: 'Atrakcje', href: '/#amenities', is_route: false },
    { name: 'Basen Balia Sauna', href: '/spa', is_route: true },
    { name: 'W okolicy', href: '/#attractions', is_route: false },
    { name: 'Kontakt', href: '/#contact', is_route: false },
    { name: 'Regulamin', href: '/regulamin', is_route: true },
  ];

  const navLinks = menuItems.length > 0 
    ? menuItems.filter(item => item.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0))
    : defaultLinks;

  const logoUrl = siteLogo || "https://customer-assets.emergentagent.com/job_rustic-retreat-7/artifacts/gtmkak5t_5556a.png";

  // Helper to get href properly (API uses href, may include /#)
  const getHref = (link) => {
    if (link.href?.startsWith('/#')) return link.href.substring(1);
    return link.href;
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-soft' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={logoUrl}
              alt="VIP VILLAGE Logo" 
              className="h-24 md:h-28 w-auto object-contain"
            />
            <div className={`transition-colors duration-300 ${isScrolled ? 'text-forest' : 'text-white'}`}>
              <span className="font-heading text-xl font-semibold">VIP VILLAGE</span>
              <p className="text-xs font-body opacity-80">Las • Woda • Rower</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              link.is_route || link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`font-body text-sm tracking-wide transition-colors duration-300 hover:text-terracotta ${
                    isScrolled ? 'text-forest' : 'text-white'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </Link>
              ) : (
                <a 
                  key={link.name}
                  href={getHref(link)}
                  className={`font-body text-sm tracking-wide transition-colors duration-300 hover:text-terracotta ${
                    isScrolled ? 'text-forest' : 'text-white'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </a>
              )
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? 'text-forest' : 'text-white'} />
            ) : (
              <Menu className={isScrolled ? 'text-forest' : 'text-white'} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white absolute top-20 left-0 right-0 shadow-lg">
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                link.is_route || link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="block font-body text-forest py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a 
                    key={link.name}
                    href={getHref(link)}
                    className="block font-body text-forest py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                )
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// ============== HERO ==============
const HeroSection = ({ content }) => (
  <section className="relative h-screen" data-testid="hero-section">
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ 
        backgroundImage: `url(${content?.hero_image || 'https://static.wixstatic.com/media/9ecdfe_7d76c9c4dd3c4503a3f094ba70074451~mv2.jpg'})`,
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-forest/60 via-forest/40 to-forest/80" />
    </div>
    
    <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white mb-6 animate-fade-in">
        {content?.hero_title || 'Luksus w objęciach natury'}
      </h1>
      {/* Mobile: add background frame for better readability */}
      <p className="font-body text-lg md:text-xl text-white/90 max-w-2xl mb-8 animate-fade-in delay-200 
                    bg-black/30 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none 
                    px-4 py-3 md:px-0 md:py-0 rounded-lg md:rounded-none">
        {content?.hero_subtitle || 'Odkryj spokój i komfort w sercu Świętokrzyskiego. Domki, stodoły i apartamenty bezpośrednio przy lesie.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 animate-fade-in delay-300">
        <a href="#offer">
          <Button 
            className="bg-terracotta hover:bg-terracotta-dark text-white rounded-none px-10 py-6 text-lg font-heading tracking-wider"
            data-testid="hero-offer-btn"
          >
            Zobacz ofertę
          </Button>
        </a>
        <a href="#contact">
          <Button 
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-forest rounded-none px-10 py-6 text-lg font-heading tracking-wider bg-transparent"
            data-testid="hero-contact-btn"
          >
            Kontakt
          </Button>
        </a>
      </div>
    </div>
  </section>
);

// ============== FEATURES BAR ==============
const FeaturesBar = ({ features }) => {
  const defaultFeatures = [
    { icon: 'trees', text: 'Bezpośrednio przy lesie' },
    { icon: 'waves', text: '3 km do jeziora Sielpia' },
    { icon: 'bike', text: 'Szlak Green Velo' },
    { icon: 'users', text: 'Idealny dla rodzin' },
  ];

  const displayFeatures = features?.length > 0 ? features : defaultFeatures;

  const getIcon = (iconName) => {
    const icons = {
      trees: TreePine,
      waves: Waves,
      bike: Bike,
      users: Users,
      wifi: Wifi,
      car: Car,
    };
    return icons[iconName] || TreePine;
  };

  return (
    <section className="bg-forest py-8" data-testid="features-bar">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {displayFeatures.map((feature, index) => {
            const IconComponent = getIcon(feature.icon);
            return (
              <div key={index} className="flex items-center justify-center gap-3 text-white">
                <IconComponent className="w-6 h-6 text-terracotta" />
                <span className="font-body text-sm md:text-base">{feature.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============== ABOUT ==============
const AboutSection = ({ content, socialLinks = [] }) => {
  const getSocialIcon = (name) => {
    switch (name) {
      case 'instagram': return <Instagram className="w-5 h-5" />;
      case 'facebook': return <Facebook className="w-5 h-5" />;
      case 'tiktok': return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>;
      case 'google': return <Globe className="w-5 h-5" />;
      case 'booking': return <ExternalLink className="w-5 h-5" />;
      default: return <Globe className="w-5 h-5" />;
    }
  };

  return (
    <section id="about" className="py-20 md:py-32 bg-sand" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="space-y-6 animate-fade-in">
            <span className="font-accent text-3xl text-terracotta">
              {content?.about_label || 'Nasza historia'}
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-forest">
              {content?.about_title || 'Witaj w VIP Village Agromiedzierza'}
            </h2>
            <p className="font-body text-forest/80 leading-relaxed">
              {content?.about_text1 || 'Agroturystyka VIP VILLAGE oferuje możliwość całorocznego zakwaterowania. Do dyspozycji Gości są domki, stodoły, apartamenty oraz pokoje. Oferta skierowana jest szczególnie do rodzin z dziećmi oraz osób ceniących spokojny wypoczynek w bliskości natury.'}
            </p>
            <p className="font-body text-forest/80 leading-relaxed">
              {content?.about_text2 || 'Jesteśmy w miejscowości Miedzierza, województwo Świętokrzyskie, 3 km od zalewu Sielpia. Czyste powietrze i malownicze krajobrazy przyciągają naszych Gości.'}
            </p>
            
            {/* Social Media Icons */}
            <div className="pt-6">
              <p className="font-heading text-lg text-forest mb-3">Dołącz do nas</p>
              <div className="flex gap-4">
                {socialLinks.filter(link => link.enabled && link.url).map((link) => (
                  <a 
                    key={link.id}
                    href={link.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-forest/10 rounded-full flex items-center justify-center hover:bg-terracotta hover:text-white text-forest transition-colors"
                    title={link.name.charAt(0).toUpperCase() + link.name.slice(1)}
                  >
                    {getSocialIcon(link.name)}
                  </a>
                ))}
                {socialLinks.filter(link => link.enabled && link.url).length === 0 && (
                  <>
                    <a 
                      href="https://www.instagram.com/vipvillage" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-forest/10 rounded-full flex items-center justify-center hover:bg-terracotta hover:text-white text-forest transition-colors"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a 
                      href="https://www.facebook.com/VIP-Village-1372695046171760/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-forest/10 rounded-full flex items-center justify-center hover:bg-terracotta hover:text-white text-forest transition-colors"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img 
                src={content?.about_image1 || 'https://static.wixstatic.com/media/9ecdfe_ac682a24e5fe497fbcac4c16a89dc299~mv2.jpg'}
                alt="Wnętrze" 
                className="w-full h-64 object-cover shadow-soft"
              />
              <img 
                src={content?.about_image2 || 'https://static.wixstatic.com/media/9ecdfe_82d72d7073914b9b8f8d0095dd72e6e8~mv2.jpg'}
                alt="Obiekt" 
                className="w-full h-64 object-cover shadow-soft mt-8"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-terracotta text-white p-6 shadow-lg">
              <span className="font-heading text-4xl font-bold">{content?.about_stat_number || '6'}</span>
              <p className="font-body text-sm">{content?.about_stat_text || 'typów zakwaterowania'}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== OFFER ==============
const OfferSection = ({ accommodations, content }) => {
  return (
    <section id="offer" className="py-20 md:py-32 bg-white" data-testid="offer-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="font-accent text-3xl text-terracotta">{content?.offer_label || 'Nasza oferta'}</span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-forest mt-2">
            {content?.offer_title || 'wybierz swoje miejsce'}
          </h2>
          <p className="font-body text-forest/70 mt-4 max-w-2xl mx-auto">
            {content?.offer_subtitle || 'Od przytulnych pokoi po przestronne stodoły — mamy coś dla każdego.'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {accommodations.map((acc, index) => (
            <Card 
              key={acc.id} 
              className="bg-white border-0 shadow-soft hover:-translate-y-2 transition-all duration-500 overflow-hidden group"
              data-testid={`accommodation-card-${index}`}
            >
              <div className="relative h-56 overflow-hidden">
                <Link to={`/accommodation/${acc.id}`}>
                  <img 
                    src={acc.images?.[0] || 'https://static.wixstatic.com/media/9ecdfe_7d76c9c4dd3c4503a3f094ba70074451~mv2.jpg'}
                    alt={acc.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </Link>
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1">
                  <span className="font-body text-sm text-forest">do {acc.capacity} osób</span>
                </div>
                <Link 
                  to={`/accommodation/${acc.id}`}
                  className="absolute bottom-4 left-4 bg-terracotta/90 hover:bg-terracotta text-white px-3 py-1.5 text-sm font-body flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Eye className="w-4 h-4" /> Zobacz szczegóły
                </Link>
              </div>
              <CardContent className="p-6">
                <Link to={`/accommodation/${acc.id}`}>
                  <h3 className="font-heading text-xl text-forest mb-2 hover:text-terracotta transition-colors">{acc.name}</h3>
                </Link>
                <p className="font-body text-forest/70 text-sm mb-4 line-clamp-2">
                  {acc.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-sand-dark">
                  <div>
                    <span className="font-body text-sm text-forest/60">od</span>
                    <span className="font-heading text-2xl text-forest ml-1">
                      {acc.price_low_season} zł
                    </span>
                    <span className="font-body text-sm text-forest/60">/noc</span>
                  </div>
                  <a href="#contact">
                    <Button 
                      variant="ghost" 
                      className="text-terracotta hover:text-terracotta-dark hover:bg-terracotta/10 gap-1"
                    >
                      Zapytaj <ChevronRight className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============== AMENITIES (Na terenie) ==============
const AmenitiesSection = ({ amenities, content }) => {
  const defaultAmenities = [
    { icon: 'tent', text: 'Altany ogrodowe', free: true },
    { icon: 'flame', text: 'Grille, miejsce na ognisko', free: true },
    { icon: 'waves', text: 'Tor pontonowy', free: true },
    { icon: 'waves', text: 'Basen', free: true },
    { icon: 'dumbbell', text: 'Tyrolka', free: true },
    { icon: 'gamepad', text: 'Gokarty', free: true },
    { icon: 'baby', text: 'Place zabaw', free: true },
    { icon: 'dumbbell', text: 'Speed Ball', free: true },
    { icon: 'dumbbell', text: 'Trampolina, boisko', free: true },
    { icon: 'tent', text: 'Hamaki, leżaki, huśtawki', free: true },
    { icon: 'bike', text: 'Rowery', free: true },
    { icon: 'dumbbell', text: 'Slack Line', free: true },
    { icon: 'wifi', text: 'Wi-Fi, Parking', free: true },
    { icon: 'waves', text: 'Podgrzewany Basen Premium', free: true, note: 'maj-listopad' },
    { icon: 'sparkles', text: 'Piana Party', free: true, note: 'wakacje - niedziela' },
    { icon: 'sparkles', text: 'Bańki z dymem', free: true, note: 'wakacje - wtorek' },
    { icon: 'film', text: 'Kino letnie', free: true, note: 'wakacje - czwartek' },
    { icon: 'bath', text: 'Gorąca Balia', free: false },
    { icon: 'thermometer', text: 'Sauna', free: false },
  ];

  const displayAmenities = amenities?.length > 0 ? amenities : defaultAmenities;

  const getIcon = (iconName) => {
    const icons = {
      tent: Tent,
      flame: Flame,
      waves: Waves,
      dumbbell: Dumbbell,
      gamepad: Gamepad2,
      baby: Baby,
      bike: Bike,
      wifi: Wifi,
      sparkles: Sparkles,
      film: Film,
      bath: Bath,
      thermometer: ThermometerSun,
    };
    return icons[iconName] || Sparkles;
  };

  const freeAmenities = displayAmenities.filter(a => a.free !== false);
  const paidAmenities = displayAmenities.filter(a => a.free === false);

  return (
    <section id="amenities" className="py-20 md:py-32 bg-sand" data-testid="amenities-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="font-accent text-3xl text-terracotta">{content?.amenities_label || 'Atrakcje na terenie'}</span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-forest mt-2">
            {content?.amenities_title || 'do dyspozycji gości'}
          </h2>
        </div>

        {/* Free amenities */}
        <div className="mb-12">
          <h3 className="font-heading text-xl text-forest mb-6 text-center">
            <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-body">
              Bezpłatnie
            </span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {freeAmenities.map((amenity, index) => {
              const IconComponent = getIcon(amenity.icon);
              return (
                <div key={index} className="bg-white p-4 rounded shadow-soft flex items-center gap-3 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-terracotta" />
                  </div>
                  <div>
                    <span className="font-body text-forest text-sm">{amenity.text}</span>
                    {amenity.note && (
                      <p className="font-body text-xs text-forest/50">{amenity.note}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Paid amenities */}
        {paidAmenities.length > 0 && (
          <div className="flex flex-col items-center">
            <h3 className="font-heading text-xl text-forest mb-6 text-center">
              <span className="bg-terracotta/20 text-terracotta px-4 py-2 rounded-full text-sm font-body">
                za dodatkową opłatą
              </span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 justify-items-center">
              {paidAmenities.map((amenity, index) => {
                const IconComponent = getIcon(amenity.icon);
                return (
                  <div key={index} className="bg-white p-4 rounded shadow-soft flex items-center gap-3 border-2 border-terracotta/20 min-w-[160px]">
                    <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-terracotta" />
                    </div>
                    <span className="font-body text-forest text-sm">{amenity.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// ============== GALLERY ==============
const GallerySection = ({ images, content, categories: apiCategories = [] }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');

  const defaultImages = [
    { src: 'https://static.wixstatic.com/media/9ecdfe_9c1788034f684e26bbfca9d484762aac~mv2.jpg', title: 'Krajobraz', category: 'teren' },
    { src: 'https://static.wixstatic.com/media/9ecdfe_6bcf62e561c344dda31e0524d2db16d5~mv2.jpg', title: 'Stodoła', category: 'obiekty' },
    { src: 'https://static.wixstatic.com/media/9ecdfe_f2484810052445cc96774efd72b9fa2e~mv2.jpg', title: 'Domki', category: 'obiekty' },
    { src: 'https://static.wixstatic.com/media/9ecdfe_452fb696dd1942e683ba9fc7e796137d~mv2.jpg', title: 'Tyrolka', category: 'atrakcje' },
    { src: 'https://static.wixstatic.com/media/9ecdfe_d25639c3083b4c3d82765d210d7cc046~mv2.jpg', title: 'Sauna', category: 'spa' },
    { src: 'https://static.wixstatic.com/media/9ecdfe_7d76c9c4dd3c4503a3f094ba70074451~mv2.jpg', title: 'Balia', category: 'spa' },
  ];

  const displayImages = images?.length > 0 ? images : defaultImages;
  
  // Filter by selected category
  const filteredImages = activeCategory === 'all' 
    ? displayImages.filter(img => img.show_on_homepage !== false)
    : displayImages.filter(img => img.category === activeCategory);

  // Build category labels from API or use defaults
  const categoryLabels = {
    'all': 'Wszystkie',
    ...apiCategories.reduce((acc, cat) => {
      acc[cat.name] = cat.label;
      return acc;
    }, {})
  };

  // Get unique categories from images that exist in our category list
  const availableCategories = ['all', ...new Set(displayImages.map(img => img.category).filter(Boolean))];

  return (
    <section id="gallery" className="py-20 md:py-32 bg-white" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="font-accent text-3xl text-terracotta">{content?.gallery_label || 'Galeria'}</span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-forest mt-2">
            {content?.gallery_title || 'zobacz nasze miejsce'}
          </h2>
          <p className="font-body text-lg text-forest/70 mt-4 max-w-2xl mx-auto">
            {content?.gallery_subtitle || 'Odkryj urokliwe zakątki VIP Village. Nasze domki, stodoły i apartamenty otoczone są przepiękną przyrodą Świętokrzyskiego.'}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {availableCategories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full font-body text-sm transition-all ${
                activeCategory === cat 
                  ? 'bg-forest text-white' 
                  : 'bg-sand text-forest hover:bg-forest/10'
              }`}
            >
              {categoryLabels[cat] || cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {filteredImages.slice(0, 6).map((img, index) => (
            <button 
              key={img.id || index}
              onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }}
              className={`relative overflow-hidden group cursor-pointer ${
                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
            >
              <img 
                src={img.src || img}
                alt={img.alt_text || img.title || `Zdjęcie galerii ${index + 1}`}
                className={`w-full object-cover group-hover:scale-110 transition-transform duration-700 ${
                  index === 0 ? 'h-[400px] md:h-full' : 'h-48 md:h-64'
                }`}
              />
              <div className="absolute inset-0 bg-forest/0 group-hover:bg-forest/40 transition-all duration-300 flex items-center justify-center">
                <span className="text-white font-body opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                  <Image className="w-5 h-5" /> {img.title || 'Zobacz'}
                </span>
              </div>
              {/* Category labels hidden on homepage as per user request */}
            </button>
          ))}
        </div>

        {filteredImages.length > 6 && (
          <div className="text-center mt-8">
            <Link to="/gallery">
              <Button className="bg-forest hover:bg-forest-light text-white rounded-none px-8 py-4 font-heading tracking-wider gap-2">
                <Image className="w-5 h-5" /> Zobacz wszystkie zdjęcia ({displayImages.length})
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-0">
          <div className="relative w-full h-[90vh] flex items-center justify-center">
            {displayImages[lightboxIndex] && (
              <>
                <img
                  src={displayImages[lightboxIndex].src || displayImages[lightboxIndex]}
                  alt={displayImages[lightboxIndex].alt_text || displayImages[lightboxIndex].title || 'Zdjęcie galerii'}
                  className="max-w-full max-h-full object-contain"
                />
                <button
                  onClick={() => setLightboxOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-forest" />
                </button>
                <button
                  onClick={() => setLightboxIndex((lightboxIndex - 1 + displayImages.length) % displayImages.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center"
                >
                  <ChevronLeft className="w-6 h-6 text-forest" />
                </button>
                <button
                  onClick={() => setLightboxIndex((lightboxIndex + 1) % displayImages.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center"
                >
                  <ChevronRight className="w-6 h-6 text-forest" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full font-body">
                  {lightboxIndex + 1} / {displayImages.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ============== SPA (Balia, Basen, Sauna) ==============
const SpaSection = ({ content }) => (
  <section id="spa" className="py-20 md:py-32 bg-forest text-white" data-testid="spa-section">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <span className="font-accent text-3xl text-terracotta">{content?.spa_label || 'Relaks i wypoczynek'}</span>
        <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl mt-2">
          {content?.spa_title || 'Balia • Basen • Sauna'}
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Balia */}
        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
          <div className="w-20 h-20 bg-terracotta rounded-full flex items-center justify-center mx-auto mb-4">
            <Bath className="w-10 h-10 text-white" />
          </div>
          <h3 className="font-heading text-2xl mb-2">{content?.balia_name || 'Gorąca Balia'}</h3>
          <p className="font-body text-white/70 mb-4">
            {content?.balia_text || 'Relaks w gorącej balii pod gwiazdami. Idealne na romantyczny wieczór lub spotkanie w gronie przyjaciół.'}
          </p>
          <p className="font-heading text-terracotta text-xl">
            {content?.balia_price || 'od 150 zł / 2h'}
          </p>
        </div>

        {/* Basen */}
        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
          <div className="w-20 h-20 bg-terracotta rounded-full flex items-center justify-center mx-auto mb-4">
            <Waves className="w-10 h-10 text-white" />
          </div>
          <h3 className="font-heading text-2xl mb-2">{content?.basen_name || 'Basen Premium'}</h3>
          <p className="font-body text-white/70 mb-4">
            {content?.basen_text || 'Podgrzewany basen dostępny od maja do listopada. Idealne miejsce na letni wypoczynek.'}
          </p>
          <p className="font-heading text-terracotta text-xl">
            {content?.basen_price || 'w cenie pobytu (sezon)'}
          </p>
        </div>

        {/* Sauna */}
        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
          <div className="w-20 h-20 bg-terracotta rounded-full flex items-center justify-center mx-auto mb-4">
            <ThermometerSun className="w-10 h-10 text-white" />
          </div>
          <h3 className="font-heading text-2xl mb-2">{content?.sauna_name || 'Sauna'}</h3>
          <p className="font-body text-white/70 mb-4">
            {content?.sauna_text || 'Tradycyjna sauna fińska. Doskonały sposób na relaks i oczyszczenie organizmu.'}
          </p>
          <p className="font-heading text-terracotta text-xl">
            {content?.sauna_price || 'od 100 zł / 2h'}
          </p>
        </div>
      </div>

      <div className="text-center mt-12">
        <a href="#contact">
          <Button className="bg-terracotta hover:bg-terracotta-dark text-white rounded-none px-10 py-4 font-heading tracking-wider">
            Zarezerwuj termin
          </Button>
        </a>
      </div>
    </div>
  </section>
);

// ============== ATTRACTIONS CAROUSEL ==============
const AttractionsSection = ({ attractions, content }) => {
  const scrollRef = useRef(null);
  
  const defaultAttractions = [
    { name: 'Jezioro Sielpia', distance: '3 km', image: 'https://static.wixstatic.com/media/9ecdfe_420549fc940f4e7c9485a157ebcf1e83~mv2_d_2502_1300_s_2.jpg' },
    { name: 'Jaskinia Raj', distance: '25 km', image: 'https://static.wixstatic.com/media/9ecdfe_5edfa096cb5c4da694891513bb1b4b55~mv2.jpg' },
    { name: 'Zamek w Chęcinach', distance: '35 km', image: 'https://static.wixstatic.com/media/9ecdfe_7bd43fb86b0c4b1cba2c9ea79ecf79a8~mv2.jpg' },
    { name: 'Święty Krzyż', distance: '40 km', image: 'https://static.wixstatic.com/media/9ecdfe_96db995cc5c549dea065712a7e5895fd~mv2.jpg' },
  ];

  const displayAttractions = attractions?.length > 0 ? attractions : defaultAttractions;

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="attractions" className="py-20 md:py-32 bg-sand relative" data-testid="attractions-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="font-accent text-3xl text-terracotta">{content?.attractions_label || 'Odkryj okolicę'}</span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-forest mt-2">
            {content?.attractions_title || 'atrakcje w pobliżu'}
          </h2>
        </div>

        <div className="relative">
          {/* Scroll buttons */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-sand transition-colors -ml-6"
          >
            <ChevronLeft className="w-6 h-6 text-forest" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-sand transition-colors -mr-6"
          >
            <ChevronRight className="w-6 h-6 text-forest" />
          </button>

          {/* Carousel */}
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 px-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {displayAttractions.map((attraction, index) => {
              const CardWrapper = attraction.link ? 'a' : 'div';
              const cardProps = attraction.link ? { 
                href: attraction.link, 
                target: '_blank', 
                rel: 'noopener noreferrer' 
              } : {};
              
              return (
                <CardWrapper 
                  key={index} 
                  {...cardProps}
                  className="flex-shrink-0 w-72 group cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden rounded-lg mb-4">
                    <img 
                      src={attraction.image}
                      alt={attraction.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-forest/80 to-transparent p-4">
                      <span className="text-white/80 text-sm font-body flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {attraction.distance}
                      </span>
                    </div>
                    {attraction.link && (
                      <div className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ExternalLink className="w-4 h-4 text-forest" />
                      </div>
                    )}
                  </div>
                  <h3 className="font-heading text-lg text-forest group-hover:text-terracotta transition-colors flex items-center gap-2">
                    {attraction.name}
                    {attraction.link && <ExternalLink className="w-4 h-4 opacity-50" />}
                  </h3>
                  {attraction.description && (
                    <p className="font-body text-sm text-forest/60 mt-1 line-clamp-2">{attraction.description}</p>
                  )}
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Decorative wave separator */}
      <div className="absolute -bottom-1 left-0 right-0 overflow-hidden leading-none">
        <svg className="relative block w-full h-12 md:h-16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
                opacity=".25" 
                className="fill-white"></path>
          <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" 
                opacity=".5" 
                className="fill-white"></path>
          <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" 
                className="fill-white"></path>
        </svg>
      </div>
    </section>
  );
};

// ============== GOOGLE REVIEWS ==============
const ReviewsSection = ({ reviews, averageRating, totalReviews, enabled }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Number of visible cards (3 on desktop, 1 on mobile)
  const visibleCards = isMobile ? 1 : 3;
  // Max index we can scroll to
  const maxIndex = Math.max(0, reviews.length - visibleCards);

  useEffect(() => {
    if (!enabled || reviews.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [reviews.length, enabled, maxIndex]);

  if (!enabled || reviews.length === 0) return null;

  const renderStars = (rating, size = 'w-5 h-5') => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`${size} ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
    ));
  };

  const nextSlide = () => setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  const prevSlide = () => setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));

  const googleReviewsUrl = "https://www.google.com/search?client=firefox-b-d&hs=fWLp&sca_esv=190a96a9d156750b&sxsrf=ANbL-n79d-qta7R6N15-ESar4Xaz0m3Nug:1771013873342&q=agromiedzierza&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOSzt3ZG-vDIfBsQQpPB1Z-w-BM7jFVX5cV3jbvvASUh9pA9x0QxDPdVGNRGN-YIzTDY4Q9Y%3D&uds=ALYpb_mM1P_6lpjjfowRxWEKPgkkbZ9Fp9G9IgX9aXahACpuq9e1XcSuWEwxsjNIErrWainWEKdbzi_gYveR2XPyffC-70q0PZXVdBpf1_wJsbeq7FmGflM&sa=X&ved=2ahUKEwjI_7_mpNeSAxUgLhAIHZZsKnYQ3PALegQIIRAE&biw=2560&bih=919&dpr=1";

  // Calculate card width percentage
  const cardWidthPercent = 100 / visibleCards;

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-sand to-white" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-soft mb-6">
            <Star className="w-7 h-7 text-yellow-400 fill-yellow-400" />
            <span className="font-heading text-xl text-forest">Opinie naszych gości</span>
          </div>
          <div className="flex items-center justify-center gap-4">
            <div className="flex gap-1">{renderStars(Math.round(averageRating), 'w-7 h-7')}</div>
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-4xl text-forest">{averageRating}</span>
              <span className="font-body text-forest/60 text-lg">/ 5</span>
            </div>
          </div>
          <p className="font-body text-forest/50 mt-2">Na podstawie {totalReviews} opinii</p>
        </div>

        {/* Reviews Slider */}
        <div className="relative">
          {/* Navigation Arrows */}
          {reviews.length > visibleCards && (
            <>
              <button 
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-4 z-10 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-forest hover:text-white transition-all"
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-4 z-10 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-forest hover:text-white transition-all"
              >
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </>
          )}

          {/* Slider Container */}
          <div className="overflow-hidden mx-6 md:mx-8">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * cardWidthPercent}%)` }}
            >
              {reviews.map((review, idx) => (
                <div 
                  key={review.id || idx} 
                  className="flex-shrink-0 px-2 md:px-3"
                  style={{ width: `${cardWidthPercent}%` }}
                >
                  <div 
                    className="bg-white p-5 md:p-6 rounded-2xl shadow-soft border border-sand hover:shadow-lg transition-shadow cursor-pointer h-full"
                    onClick={() => setSelectedReview(review)}
                  >
                    {/* Author Header */}
                    <div className="flex items-center gap-3 md:gap-4 mb-4">
                      {review.profile_photo_url ? (
                        <img 
                          src={review.profile_photo_url} 
                          alt={review.author_name}
                          className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-sand"
                        />
                      ) : (
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-forest to-forest-light rounded-full flex items-center justify-center border-2 border-sand flex-shrink-0">
                          <Users className="w-6 h-6 md:w-7 md:h-7 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-body font-semibold text-forest truncate">{review.author_name}</p>
                        <p className="font-body text-xs text-forest/50">
                          {new Date(review.time).toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    
                    {/* Stars */}
                    <div className="flex gap-0.5 mb-3">{renderStars(review.rating, 'w-4 h-4')}</div>
                    
                    {/* Review Text */}
                    <p className="font-body text-forest/80 text-sm leading-relaxed line-clamp-4">
                      {review.text}
                    </p>
                    
                    {/* Read more hint */}
                    {review.text.length > 150 && (
                      <p className="font-body text-forest/40 text-xs mt-2 hover:text-forest transition-colors">
                        Kliknij, aby przeczytać więcej...
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots indicator */}
          {reviews.length > visibleCards && (
            <div className="flex justify-center gap-1.5 mt-8">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-forest w-6' : 'bg-forest/30 w-2'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <a 
            href={googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-forest hover:bg-forest-light text-white px-6 py-3 rounded-full font-body transition-all shadow-soft hover:shadow-lg"
          >
            <img src="https://www.gstatic.com/images/branding/product/2x/googleg_48dp.png" alt="Google" className="w-5 h-5" />
            Zobacz wszystkie opinie
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Review Detail Popup */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedReview && (
            <div className="pt-2">
              {/* Author */}
              <div className="flex items-center gap-4 mb-4">
                {selectedReview.profile_photo_url ? (
                  <img 
                    src={selectedReview.profile_photo_url} 
                    alt={selectedReview.author_name}
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover border-2 border-sand flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-forest to-forest-light rounded-full flex items-center justify-center border-2 border-sand flex-shrink-0">
                    <Users className="w-7 h-7 md:w-8 md:h-8 text-white" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-heading text-lg md:text-xl text-forest">{selectedReview.author_name}</p>
                  <p className="font-body text-sm text-forest/50">
                    {new Date(selectedReview.time).toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              </div>
              
              {/* Stars */}
              <div className="flex gap-1 mb-4">{renderStars(selectedReview.rating, 'w-5 h-5 md:w-6 md:h-6')}</div>
              
              {/* Full Review Text */}
              <p className="font-body text-forest/80 leading-relaxed whitespace-pre-wrap text-sm md:text-base">
                {selectedReview.text}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ============== CONTACT WITH MAP ==============
const ContactSection = ({ content, socialLinks = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Wiadomość wysłana!', {
        description: 'Odpowiemy najszybciej jak to możliwe.'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Błąd wysyłania', {
        description: 'Spróbuj ponownie później.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-forest text-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <span className="font-accent text-3xl text-terracotta">{content?.contact_label || 'Skontaktuj się'}</span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl mt-2 mb-8">
              {content?.contact_title || 'Masz pytania?'}
            </h2>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-terracotta" />
                </div>
                <div>
                  <p className="font-body text-white/60 text-sm">Adres</p>
                  <p className="font-body">{content?.address || 'Miedzierza 110, 26-212 Smyków'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-terracotta" />
                </div>
                <div>
                  <p className="font-body text-white/60 text-sm">Telefon</p>
                  <a href={`tel:${content?.phone || '+48605737885'}`} className="font-body hover:text-terracotta transition-colors">
                    {content?.phone || '+48 605 737 885'}
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-terracotta" />
                </div>
                <div>
                  <p className="font-body text-white/60 text-sm">Email</p>
                  <a href={`mailto:${content?.email || 'agromiedzierza@gmail.com'}`} className="font-body hover:text-terracotta transition-colors">
                    {content?.email || 'agromiedzierza@gmail.com'}
                  </a>
                </div>
              </div>
            </div>

            {/* Nasza Historia */}
            {content?.our_history && (
              <div className="mt-10 pt-8 border-t border-white/20">
                <h3 className="font-heading text-xl mb-4 text-terracotta">Nasza historia</h3>
                <div 
                  className="font-body text-white/80 leading-relaxed prose prose-invert prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: content.our_history }}
                />
              </div>
            )}
          </div>

          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Input 
                  placeholder="Imię i nazwisko"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none h-14"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input 
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none h-14"
                />
                <Input 
                  type="tel"
                  placeholder="Telefon"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none h-14"
                />
              </div>
              <Textarea 
                placeholder="Twoja wiadomość..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={5}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none resize-none"
              />
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-terracotta hover:bg-terracotta-dark text-white rounded-none h-14 font-heading tracking-wider text-lg"
              >
                {isSubmitting ? 'Wysyłanie...' : 'Wyślij wiadomość'}
              </Button>
            </form>
          </div>
        </div>

        {/* Google Maps */}
        <div className="mt-16">
          <div className="relative">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d40000!2d20.3802489!3d51.0864844!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4719cdf1c04fc5f9%3A0x11a22796d7c9e1e0!2sAGROTURYSTYKA%20VIP%20VILLAGE%20LAS%20WODA%20ROWER!5e0!3m2!1spl!2spl!4v1"
              width="100%"
              height="400"
              style={{ border: 0, filter: 'grayscale(20%)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`Lokalizacja VIP Village - ${content?.address || 'Miedzierza 110'}`}
              className="rounded-lg"
            />
            <a 
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(content?.address || 'Miedzierza 110, 26-212 Smyków, Polska')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 right-4 bg-forest hover:bg-forest-light text-white px-6 py-3 rounded-lg font-body flex items-center gap-2 shadow-lg transition-all"
            >
              <MapPin className="w-5 h-5" />
              Wyznacz trasę
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== REGULAMIN ==============
const RegulaminSection = ({ content }) => (
  <section id="regulamin" className="py-20 md:py-32 bg-sand" data-testid="regulamin-section">
    <div className="max-w-4xl mx-auto px-4">
      <div className="text-center mb-12">
        <span className="font-accent text-3xl text-terracotta">Zasady pobytu</span>
        <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-forest mt-2">
          Regulamin
        </h2>
      </div>

      <div className="bg-white p-8 rounded shadow-soft">
        <div className="prose prose-forest max-w-none font-body text-forest/80">
          {content?.regulamin ? (
            <div dangerouslySetInnerHTML={{ __html: content.regulamin }} />
          ) : (
            <div className="space-y-4">
              <h3 className="font-heading text-xl text-forest">1. Doba hotelowa</h3>
              <p>Doba trwa od godziny 14:00 do godziny 10:00 dnia następnego.</p>
              
              <h3 className="font-heading text-xl text-forest">2. Rezerwacja</h3>
              <p>Rezerwacja jest ważna po wpłacie zadatku w wysokości 30% wartości pobytu.</p>
              
              <h3 className="font-heading text-xl text-forest">3. Cisza nocna</h3>
              <p>Cisza nocna obowiązuje od godziny 22:00 do 7:00.</p>
              
              <h3 className="font-heading text-xl text-forest">4. Zwierzęta</h3>
              <p>Akceptujemy zwierzęta domowe po wcześniejszym uzgodnieniu.</p>
              
              <h3 className="font-heading text-xl text-forest">5. Odpowiedzialność</h3>
              <p>Za szkody wyrządzone przez Gości odpowiedzialność ponosi osoba dokonująca rezerwacji.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
);

// ============== FOOTER ==============
const Footer = ({ siteLogo }) => {
  const logoUrl = siteLogo || "https://customer-assets.emergentagent.com/job_rustic-retreat-7/artifacts/gtmkak5t_5556a.png";
  
  return (
    <footer className="bg-forest-dark py-8 text-white/60" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <img 
              src={logoUrl}
              alt="VIP Village" 
              className="h-10 w-auto object-contain"
            />
            <span className="font-heading text-white">VIP Village Agromiedzierza</span>
          </div>
          <p className="font-body text-sm">
            © {new Date().getFullYear()} Agromiedzierza. Wszelkie prawa zastrzeżone.
          </p>
          <Link 
            to="/admin" 
            className="font-body text-sm hover:text-terracotta transition-colors"
            data-testid="admin-link"
          >
            Panel administratora
          </Link>
        </div>
      </div>
    </footer>
  );
};

// ============== MAIN HOMEPAGE ==============
const HomePage = () => {
  const [accommodations, setAccommodations] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const [menuItems, setMenuItems] = useState([]);
  const [reviewsData, setReviewsData] = useState({ reviews: [], average_rating: 5.0, total_reviews: 0, enabled: true });
  const [promoConfig, setPromoConfig] = useState({ enabled: false });
  const [seoConfig, setSeoConfig] = useState({});
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accRes, attrRes, featRes, amenRes, galleryRes, contentRes, catRes, menuRes, reviewsRes, promoRes, seoRes, socialRes] = await Promise.all([
        axios.get(`${API}/accommodations`).catch(() => ({ data: [] })),
        axios.get(`${API}/attractions`).catch(() => ({ data: [] })),
        axios.get(`${API}/features`).catch(() => ({ data: [] })),
        axios.get(`${API}/amenities`).catch(() => ({ data: [] })),
        axios.get(`${API}/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API}/site-content`).catch(() => ({ data: {} })),
        axios.get(`${API}/gallery-categories`).catch(() => ({ data: [] })),
        axios.get(`${API}/menu-items`).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews`).catch(() => ({ data: { reviews: [], average_rating: 5.0, total_reviews: 0, enabled: true } })),
        axios.get(`${API}/promo-popup`).catch(() => ({ data: { enabled: false } })),
        axios.get(`${API}/seo-config`).catch(() => ({ data: {} })),
        axios.get(`${API}/social-links`).catch(() => ({ data: [] })),
      ]);
      
      setAccommodations(accRes.data);
      setAttractions(attrRes.data);
      setFeatures(featRes.data);
      setAmenities(amenRes.data);
      setGalleryImages(galleryRes.data);
      setSiteContent(contentRes.data);
      setGalleryCategories(catRes.data);
      setMenuItems(menuRes.data);
      setReviewsData(reviewsRes.data);
      setPromoConfig(promoRes.data);
      setSeoConfig(seoRes.data);
      setSocialLinks(socialRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Generate Schema.org JSON-LD structured data
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LodgingBusiness",
        "name": siteContent.site_name || "VIP Village Agromiedzierza",
        "description": seoConfig.meta_description || "Luksusowy wypoczynek w sercu natury. Domki, stodoły i apartamenty bezpośrednio przy lesie.",
        "url": seoConfig.canonical_url || "https://agromiedzierza.com",
        "image": seoConfig.og_image || siteContent.hero_image,
        "telephone": siteContent.contact_phone || "+48 123 456 789",
        "email": siteContent.contact_email,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": siteContent.contact_address || "Miedzierza",
          "addressLocality": "Miedzierza",
          "addressRegion": "Świętokrzyskie",
          "postalCode": "26-026",
          "addressCountry": "PL"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "50.9167",
          "longitude": "20.6333"
        },
        "priceRange": "PLN 400-600",
        ...(reviewsData.enabled && reviewsData.total_reviews > 0 ? {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": reviewsData.average_rating || 5.0,
            "bestRating": "5",
            "worstRating": "1",
            "ratingCount": reviewsData.total_reviews || 1
          }
        } : {})
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Strona główna",
            "item": seoConfig.canonical_url || "https://agromiedzierza.com"
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Helmet>
        <title>{seoConfig.meta_title || 'VIP Village Agromiedzierza - Agroturystyka w Świętokrzyskim'}</title>
        <meta name="description" content={seoConfig.meta_description || 'Luksusowy wypoczynek w sercu natury.'} />
        <meta name="keywords" content={seoConfig.meta_keywords || 'agroturystyka, świętokrzyskie'} />
        <meta name="robots" content={seoConfig.robots || 'index, follow'} />
        {seoConfig.canonical_url && <link rel="canonical" href={seoConfig.canonical_url} />}
        {seoConfig.google_site_verification && <meta name="google-site-verification" content={seoConfig.google_site_verification} />}
        {/* Open Graph */}
        <meta property="og:title" content={seoConfig.og_title || seoConfig.meta_title || 'VIP Village Agromiedzierza'} />
        <meta property="og:description" content={seoConfig.og_description || seoConfig.meta_description || ''} />
        <meta property="og:type" content="website" />
        {seoConfig.og_image && <meta property="og:image" content={seoConfig.og_image} />}
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoConfig.og_title || seoConfig.meta_title || 'VIP Village Agromiedzierza'} />
        <meta name="twitter:description" content={seoConfig.og_description || seoConfig.meta_description || ''} />
      </Helmet>
      {/* Schema.org JSON-LD - rendered outside Helmet for better compatibility */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <PromoPopup config={promoConfig} />
      <Navbar siteLogo={siteContent.site_logo} menuItems={menuItems} />
      <HeroSection content={siteContent} />
      <FeaturesBar features={features} />
      <AboutSection content={siteContent} socialLinks={socialLinks} />
      <OfferSection accommodations={accommodations} content={siteContent} />
      <AmenitiesSection amenities={amenities} content={siteContent} />
      <GallerySection images={galleryImages} content={siteContent} categories={galleryCategories} />
      <SpaSection content={siteContent} />
      <AttractionsSection attractions={attractions} content={siteContent} />
      <ReviewsSection 
        reviews={reviewsData.reviews} 
        averageRating={reviewsData.average_rating} 
        totalReviews={reviewsData.total_reviews}
        enabled={reviewsData.enabled}
      />
      <ContactSection content={siteContent} socialLinks={socialLinks} />
      <SharedFooter content={siteContent} />
    </div>
  );
};

export default HomePage;
